import { LooseValue } from '@fshr/shared/types';
import { parseISO, format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

type UseDatePickerProps = {
	onCloseDialog?: (
		isDialogOpen: boolean,
		closeFiredWith?: { key?: string; event?: unknown }
	) => void | Promise<void>;
	onOpenDialog?: (isDialogOpen: boolean) => void | Promise<void>;
	checkInDate: string | undefined;
	checkOutDate: string | undefined;
};

export function useDatePicker(props: UseDatePickerProps) {
	const { checkInDate, checkOutDate, onCloseDialog, onOpenDialog } = props;

	const [stayDate, setStayDate] = useState<string | undefined>();
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [isCheckoutDateRequired, setIsCheckoutDateRequired] = useState<boolean>(false);
	const [calendarValue, setCalendarValue] = useState<LooseValue | undefined>();
	const [startDate, setStartDateValue] = useState<Date>();
	const [endDate, setEndDateValue] = useState<Date>();
	const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
	const isDesktop = useMediaQuery({ minWidth: 1101 });
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement | null>(null);

	const updatePosition = () => {
		if (!containerRef.current) return;
		const rect = containerRef.current.getBoundingClientRect();
		const newTop = rect.bottom + window.scrollY;
		const newLeft = rect.left + window.scrollX;

		if (
			Math.abs(dropdownPosition.top - newTop) > 1 ||
			Math.abs(dropdownPosition.left - newLeft) > 1
		) {
			setDropdownPosition({ top: newTop, left: newLeft, width: rect.width });
		}
	};

	useEffect(() => {
		setStayDate(
			checkInDate && checkOutDate
				? `${format(parseISO(checkInDate) ?? '', 'MM/dd/yyyy')} - ${format(parseISO(checkOutDate) ?? '', 'MM/dd/yyyy')}`
				: undefined
		);
		setCalendarValue([parseISO(checkInDate ?? ''), parseISO(checkOutDate ?? '')]);
	}, []);

	useEffect(() => {
		if (!containerRef.current || !isOpen || !isDesktop) return;

		updatePosition();

		const resizeObserver = new ResizeObserver(updatePosition);
		resizeObserver.observe(containerRef.current);

		window.addEventListener('scroll', updatePosition, true);
		window.addEventListener('resize', updatePosition);

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener('scroll', updatePosition, true);
			window.removeEventListener('resize', updatePosition);
		};
	}, [isOpen, isDesktop]);

	useEffect(() => {
		if (inputRef.current) {
			if (!isOpen) {
				inputRef.current.focus();
			}
		}
	}, [isOpen]);

	const handleOpenDialog = () => {
		onOpenDialog?.(true);
		setIsOpen(true);
	};

	const handleCloseDialog = (escapeKeyPressed?: boolean) => {
		onCloseDialog?.(!isOpen, escapeKeyPressed ? { key: 'escape' } : undefined);
		setIsOpen(false);
	};

	return {
		stayDate,
		isCheckoutDateRequired,
		calendarValue,
		startDate,
		endDate,
		isOpen,
		dropdownPosition,
		containerRef,
		inputRef,
		isDesktop,
		setCalendarValue,
		setStayDate,
		handleOpenDialog,
		handleCloseDialog,
		setIsCheckoutDateRequired,
		setStartDateValue,
		setEndDateValue,
	};
}
